import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActive from 'src/components/Badge/BadgeActive';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import tariffTimeUnit from 'src/util/decorators/units';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminStandingChargeList component
 */
function AdminStandingChargeList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { console.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { standingCharges } = viewer;

  const intl = useIntl();

  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_standing_charge.admin_standing_charge_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_standing_charge.admin_standing_charge_list.load_more_button.state_no_more.label', defaultMessage: 'No more standing charges to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Enosi Admin - Standing Charges</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: 'Admin', path: '/admin' },
          { name: 'Standing Charges' },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">Standing Charges</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Identifier</th>
                <th scope="col">ID</th>
                <th scope="col">
                  <abbr title="External Identifier">Ext ID</abbr>
                </th>
                <th scope="col">Rate</th>
                <th scope="col">Tariff</th>
              </tr>
            </thead>
            <tbody>
              {
                // standingCharges && <pre>{JSON.stringify(standingCharges, null, 2)}</pre>
                standingCharges && standingCharges.edges && standingCharges.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    id, title, identifier, externalIdentifier, rate, timeUnit, tariff,
                  } = node;

                  return (
                    <tr key={`admin-standing-charge-${id}`}>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/standing-charges/${id}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={id} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        {`$${rate}`}
                        <abbr title={`per ${tariffTimeUnit(timeUnit)}`}>
                          {`/${tariffTimeUnit(timeUnit)}`}
                        </abbr>
                      </td>
                      <td>
                        {tariff && (
                          <>
                            <Link to={`/admin/tariffs/${tariff.id}`}>
                              {tariff.title}
                            </Link>
                            <br />
                            <BadgeActive
                              start={tariff.active.start}
                              finish={tariff.active.finish}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminStandingChargeList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminStandingChargeList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminStandingChargeList,
  {
    viewer: graphql`
      fragment AdminStandingChargeList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        standingCharges(
          first: $count
          after: $cursor
        ) @connection(key: "AdminStandingChargeList_standingCharges") {
          edges {
            cursor
            node {
              id
              externalIdentifier
              identifier
              title
              rate
              timeUnit
              tariff {
                id
                title
                active { start finish }
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.standingCharges;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminStandingChargeList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminStandingChargeList_viewer
      }
    }
  `,
  },
);
